import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { DashboardMenuItem } from 'react-admin'
import { Divider, MenuItem } from '@material-ui/core'
import BuildIcon from '@material-ui/icons/Build'
import { MyLayoutProvider, useMyLayout } from '../../../../context/useMyLayout'
import OrgLogo from '../../../../components/OrgLogo/OrgLogo'
import SelectTeam from '../../../../components/selectTeam/SelectTeam'
import MenuItemLinks from '../../../../components/MenuItemLinks/MenuItemLinks'
import User from '../../../../components/user/User'
import RoleCard from '../../../../components/RoleCard/RoleCard'
import ConditionalRender from '../../../../components/ConditionalRender/ConditionalRender'
import SIDEBAR_OPTION from '../../../../utils/constants/sidebarOptions'
import COLORS from '../../../../utils/constants/colors'
import createMenuItemLinkStyle from '../../../../utils/helpers/createMenuItemLinkStyle'
import myMenuStyles from './myMenu.styles'
import TOUR_IDS from '../../../../utils/constants/introIds'
import ImpersonationField from '../../../../components/ImpersonationField/ImpersonationField'
import ImpersonationAlert from '../../../../components/ImpersonationAlert/ImpersonationAlert'
import ORGANISATION_TYPES from '../../../../utils/constants/OrganisationTypes'
import { listDefaultOverlays } from '../../../../Apis/defaultOverlays'
import isUserSuperAdmin from '../../../../utils/helpers/isUserSuperAdmin'

const MyMenu = (props) => {
  const {
    className,
    hasDashboard,
    isSmallScreenMenuOpen,
    ...rest
  } = props
  const [showDefaultOverlays, setShowDefaultOverlays] = useState(false)
  const { myOrgs, singleOrg, error } = useMyLayout()
  const open = useSelector((state) => state.admin.ui.sidebarOpen)
  const { pathname } = useLocation()
  const history = useHistory()

  // Probe the backend's dev-only default-overlays endpoint once on mount.
  // The endpoint returns 404 outside Development, when the editor is disabled,
  // OR when the caller is not a SuperAdmin, so the nav entry stays hidden in
  // deployed environments and for non-admin users. Errors (404, 500, network
  // failures) are swallowed silently — we treat any failure as "not available".
  // skipErrorNotification suppresses the global axios error toast, since the
  // 404 is expected in deployed environments.
  // A PROD build talks to a backend that is by definition not Development, so the probe there
  // can only ever 404 — skip it rather than spend a guaranteed-failing request on every mount.
  // Every other build (the DEV image, and a local yarn start against any backend) still asks,
  // because only the backend knows whether the editor is on.
  // REACT_APP_ENABLE_DEFAULT_EDITOR=1 forces the entry on (useful for local
  // builds pointed at a remote backend) but the SuperAdmin local check still
  // applies as defense-in-depth.
  //
  // The local SuperAdmin check is redundant with the backend probe (which will
  // 404 a non-admin), but kept so the entry stays hidden if the probe
  // misbehaves (e.g. a stale 200 from a misconfigured environment).
  const isSuperAdmin = isUserSuperAdmin()
  useEffect(() => {
    if (!isSuperAdmin) {
      setShowDefaultOverlays(false)
      return undefined
    }
    if (process.env.REACT_APP_ENABLE_DEFAULT_EDITOR === '1') {
      setShowDefaultOverlays(true)
      return undefined
    }
    if (process.env.REACT_APP_RUNTIME === 'PROD') {
      setShowDefaultOverlays(false)
      return undefined
    }
    let cancelled = false
    listDefaultOverlays({ skipErrorNotification: true })
      .then(() => {
        if (!cancelled) setShowDefaultOverlays(true)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isSuperAdmin])
  const isSelectTeamVisible =
    ![...SIDEBAR_OPTION.orgMenuChoices, ...SIDEBAR_OPTION.PROFILE_ITEMS]
      .map((option) => `/${option.name}`)
      .includes(pathname) && (singleOrg?.type !== ORGANISATION_TYPES.ORG_COMPETITION && singleOrg?.type !== ORGANISATION_TYPES.ORG_FEDERATION)
  const useStyles = makeStyles((theme) => myMenuStyles(theme, isSmallScreenMenuOpen), {
    name: 'RaMenu',
  })
  const classes = useStyles(props)

  return (
    <>
      <div
        className={classnames(
          classes.main,
          {
            [classes.open]: open,
            [classes.closed]: !open,
          },
          className,
        )}
        {...rest}
      >
        <div id={TOUR_IDS.calendar.players}>
          <ConditionalRender renderValue={isSelectTeamVisible}>
            <div className='wrapper-logo-select'>
              <OrgLogo singleOrgLogo={singleOrg?.logoUrl} myOrgs={myOrgs} />
              <SelectTeam myOrgs={myOrgs} error={error} />
            </div>
          </ConditionalRender>
          {hasDashboard && <DashboardMenuItem />}
          <MenuItemLinks myOrgs={myOrgs} />
          {showDefaultOverlays && isSuperAdmin && (
            <>
              <Divider style={{ margin: '8px 16px' }} />
              <MenuItem
                onClick={() => history.push('/default-overlays')}
                style={createMenuItemLinkStyle(
                  pathname.startsWith('/default-overlays') ? 'default-overlays' : null,
                  'default-overlays',
                )}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 17 }}>
                  {pathname.startsWith('/default-overlays') ? (
                    <BuildIcon htmlColor={COLORS.orange} />
                  ) : (
                    <BuildIcon />
                  )}
                  Default overlays
                </div>
              </MenuItem>
            </>
          )}
        </div>

        <div className='wrapper-user'>
          <ImpersonationAlert sideBarOpen={open} />
          <ImpersonationField sideBarOpen={open} />
          {open && <RoleCard />}
          <User sideBarOpen={open} />
        </div>
      </div>
    </>
  )
}

MyMenu.propTypes = {
  className: PropTypes.string,
  hasDashboard: PropTypes.bool,
  isSmallScreenMenuOpen: PropTypes.bool,
}

const MyProvidedMenu = ({ open, ...props }) => {
  return (
    <MyLayoutProvider id={TOUR_IDS.calendar.orgLogo}>
      <MyMenu {...props} open={open} hasDashboard={false} />
    </MyLayoutProvider>
  )
}

MyProvidedMenu.propTypes = {
  open: PropTypes.bool,
}

export default MyProvidedMenu
